import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchBlogById, clearSingleBlog } from "../../slice/blogSlice";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { MdCalendarToday, MdEdit, MdRocket, MdArrowBack } from "react-icons/md";
import AutoInternalLinks from "../../components/AutoInternalLinks";
import { fallbackBlogs } from "./fallbackBlogsData";
import BlogContentRenderer from "./BlogContentRenderer";
import StaticBlogContent from "./StaticBlogContent";

const BlogDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const isFallback = id && id.startsWith("fb-");
  const fallbackBlog = isFallback ? fallbackBlogs.find((b) => b.id === id) : null;

  const { singleBlog, loading: apiLoading, error: apiError } = useSelector(
    (state) => state.blog
  );

  useEffect(() => {
    if (!id || id === "undefined" || isFallback) return;
    dispatch(fetchBlogById(id));
    return () => dispatch(clearSingleBlog());
  }, [dispatch, id, isFallback]);

  const loading = isFallback ? false : apiLoading;
  const error = isFallback ? null : apiError;
  const blogData = isFallback ? fallbackBlog : singleBlog;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#008300] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-semibold">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-600 text-lg">{error}</p>
      </div>
    );
  }

  if (!blogData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500 text-lg">Blog not found.</p>
      </div>
    );
  }

  const blogUrl = `https://novarsistech.com/blog/${blogData?._id || id}`;
  const blogImage =
    blogData?.image || "https://novarsistech.com/Images/novarsis-og-image.jpg";

  return (
    <>
      <Helmet>
        <title>
          {blogData?.title
            ? `${blogData.title} | Software Development Blog | Novarsis Technology`
            : "Blog Details | Novarsis Technology"}
        </title>
        <meta
          name="description"
          content={
            blogData?.description ||
            "Novarsis Technology software development blog."
          }
        />
        <meta
          name="keywords"
          content="software development blog, Novarsis Technology, web development, SEO, digital marketing"
        />
        <link rel="canonical" href={blogUrl} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={blogData.title} />
        <meta property="og:description" content={blogData.description} />
        <meta property="og:url" content={blogUrl} />
        <meta property="og:image" content={blogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blogData.title} />
        <meta name="twitter:description" content={blogData.description} />
        <meta name="twitter:image" content={blogImage} />
        <script type="application/ld+json">
          {`{
            "@context":"https://schema.org",
            "@type":"Article",
            "headline":"${blogData.title}",
            "image":"${blogImage}",
            "author":{"@type":"Organization","name":"Novarsis Technology"},
            "publisher":{"@type":"Organization","name":"Novarsis Technology","logo":{"@type":"ImageObject","url":"https://novarsistech.com/logo.png"}},
            "mainEntityOfPage":{"@type":"WebPage","@id":"${blogUrl}"}
          }`}
        </script>
        <script type="application/ld+json">
          {`{
            "@context":"https://schema.org",
            "@type":"BreadcrumbList",
            "itemListElement":[
              {"@type":"ListItem","position":1,"name":"Home","item":"https://novarsistech.com"},
              {"@type":"ListItem","position":2,"name":"Blog","item":"https://novarsistech.com/blog"},
              {"@type":"ListItem","position":3,"name":"${blogData.title}","item":"${blogUrl}"}
            ]
          }`}
        </script>
      </Helmet>

      {/* Hidden SEO */}
      <h2 className="sr-only">Software Development Blog Article by Novarsis Technology</h2>

      {/* ════════════════════════════════════════
          HERO SECTION — Left: Title & Description | Right: Image
      ════════════════════════════════════════ */}
      <section className="bg-[#f0faf0] pt-20 pb-12 px-4 md:px-10 lg:px-20">
        <div className="max-w-7xl mx-auto">

          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-sm text-gray-500 mb-8"
          >
            <Link to="/" className="hover:text-[#008300] transition">Home</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-[#008300] transition">Blog</Link>
            <span>/</span>
            <span className="text-[#008300] font-medium truncate max-w-[200px]">{blogData.title}</span>
          </motion.div>

          {/* Hero Grid — Left text | Right image */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">

            {/* LEFT — Title + Meta + Description */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="flex flex-col gap-5"
            >
              {/* Meta info */}
              <div className="flex flex-wrap items-center gap-3">
                {blogData.date && (
                  <span className="flex items-center gap-1.5 px-3 py-1 bg-[#008300]/10 text-[#008300] rounded-full text-xs font-semibold">
                    <MdCalendarToday size={18} />
                    {new Date(blogData.date).toLocaleDateString("en-GB")}
                  </span>
                )}
                {blogData.createdByUsername && (
                  <span className="flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">
                    <MdEdit size={18} />
                    {blogData.createdByUsername}
                  </span>
                )}
                <span className="flex items-center gap-1.5 px-3 py-1 bg-[#008300] text-white rounded-full text-xs font-semibold">
                  <MdRocket size={18} />
                  Article
                </span>
              </div>

              {/* Main Title */}
              <h1 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-extrabold text-gray-900 leading-tight">
                {blogData.title}
              </h1>

              {/* Divider */}
              <div className="w-16 h-1 bg-[#008300] rounded-full" />

              {/* Overview heading + Description */}
              {blogData.description && (
                <div className="space-y-2">
                  <h2 className="text-base font-bold text-gray-500 uppercase tracking-widest">
                    Overview
                  </h2>
                  <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
                    {blogData.description}
                  </p>
                </div>
              )}

              {/* Back link */}
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-[#008300] font-semibold hover:gap-3 transition-all text-sm w-fit"
              >
                <MdArrowBack size={16} />
                Back to All Blogs
              </Link>
            </motion.div>

            {/* RIGHT — Image */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5">
                <img
                  src={blogData.image}
                  alt={`${blogData.title} — Novarsis Technology Blog`}
                  className="w-full h-[300px] sm:h-[380px] lg:h-[420px] object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
              {/* Decorative accent */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#008300]/10 rounded-2xl -z-10" />
              <div className="absolute -top-4 -left-4 w-20 h-20 bg-[#008300]/5 rounded-full -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          CONTENT SECTION — Full width below hero
      ════════════════════════════════════════ */}
      <section className="bg-white py-12 px-4 md:px-10 lg:px-20">
        <div className="max-w-5xl mx-auto">

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {isFallback ? (
              /* Fallback blogs — apna original content dikhao */
              <BlogContentRenderer content={blogData.content} />
            ) : (
              /* Admin blogs — hamesha static content dikhao */
              <StaticBlogContent />
            )}
          </motion.div>

          {/* ── Footer strip ── */}
          <div className="mt-14 pt-8 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">

            {/* SEO link + date */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3 px-4 py-3 bg-[#008300]/5 border border-[#008300]/15 rounded-xl">
                <MdRocket size={24} className="text-[#008300]" />
                <p className="text-sm text-gray-700 leading-snug">
                  Explore more{" "}
                  <Link
                    to="/services"
                    className="text-[#008300] font-semibold underline underline-offset-2 hover:text-[#006600] transition"
                  >
                    software development services
                  </Link>{" "}
                  offered by Novarsis Technology.
                </p>
              </div>
              {blogData.createdAt && (
                <p className="text-xs text-gray-400 pl-1 flex items-center gap-1.5">
                  <MdCalendarToday size={12} />
                  Posted on {new Date(blogData.createdAt).toLocaleString("en-GB")}
                </p>
              )}
            </div>

            {/* Back button */}
            <Link
              to="/blog"
              className="flex-shrink-0 inline-flex items-center gap-2 bg-[#008300] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#006600] transition-all duration-300 hover:gap-3 shadow-lg"
            >
              <MdArrowBack size={18} />
              Back to Blogs
            </Link>
          </div>
        </div>
      </section>

      <AutoInternalLinks />
    </>
  );
};

export default BlogDetails;
